
import React, { useState, useEffect } from 'react';
import { Payment } from '@/services/types';
import { supabase } from '@/integrations/supabase/client';
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatDate } from '@/utils/dateUtils';
import { Loader2, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';

interface RentalPaymentsTabProps {
  rentalId: string;
  refreshRentalData?: () => void; // Сделаем этот пропс опциональным
}

interface StatusBadgeProps {
  status: string;
  statusLabels: { [key: string]: string };
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status, statusLabels }) => {
  const getBadgeVariant = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'error':
        return 'destructive';
      case 'paid':
        return 'outline';
      case 'auth':
        return 'secondary';
      default:
        return 'default';
    }
  };

  return <Badge variant={getBadgeVariant(status)}>{statusLabels[status] || status}</Badge>;
};

export const RentalPaymentsTab: React.FC<RentalPaymentsTabProps> = ({ 
  rentalId, 
  refreshRentalData 
}) => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Загрузка платежей для указанной аренды
  useEffect(() => {
    const fetchPayments = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const { data, error } = await supabase
          .from('payments')
          .select('*')
          .eq('rental_id', rentalId)
          .order('payment_date', { ascending: false });
        
        if (error) {
          throw new Error(`Ошибка при загрузке платежей: ${error.message}`);
        }
        
        // Преобразование данных из базы в формат для компонента
        const formattedPayments: Payment[] = data.map(payment => ({
            id: payment.id,
            rentalId: payment.rental_id,
            courierId: payment.courier_id,
            courierName: 'Неизвестно', // Имя курьера здесь не загружается
            amount: payment.amount,
            date: payment.payment_date,
            // Приведение к допустимым значениям статуса из типа Payment
            status: (payment.status as "error" | "paid" | "pending" | "completed" | "auth"),
            method: (payment.payment_method as 'card' | 'cash' | 'account' | 'sbp') || 'card',
            human_number: payment.human_number,
            receiptUrl: payment.receipt_url,
            subscription_id: payment.subscription_id,
        }));
        
        setPayments(formattedPayments);
      } catch (err: any) {
        console.error('Ошибка загрузки платежей:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    if (rentalId) {
      fetchPayments();
    }
  }, [rentalId]);

  // Функция для отображения метода оплаты
  const getPaymentMethodText = (method: string) => {
    switch (method) {
      case 'card': return 'Карта';
      case 'cash': return 'Наличные';
      case 'account': return 'Счет';
      case 'sbp': return 'СБП';
      default: return method;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">История платежей</h3>
      </div>
      
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Ошибка</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : payments.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          Нет платежей для этой аренды
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Дата</TableHead>
                <TableHead>Сумма</TableHead>
                <TableHead>Метод</TableHead>
                <TableHead>Статус</TableHead>
                <TableHead>Действия</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.map((payment) => (
                <TableRow key={payment.id}>
                  <TableCell>{formatDate(payment.date)}</TableCell>
                  <TableCell>{payment.amount} ₽</TableCell>
                  <TableCell>{getPaymentMethodText(payment.method)}</TableCell>
                  <TableCell>
                    <StatusBadge 
                      status={payment.status}
                      statusLabels={{
                        completed: 'Выполнен',
                        pending: 'Ожидание',
                        error: 'Ошибка',
                        paid: 'Оплачен',
                        auth: 'Авторизация'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
